import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { analyzeUploadProgress } from "./uploadProgress";

type UploadDiagnosticEvent = {
  type: string;
  elapsedMs: number;
  lengthComputable?: boolean;
  loaded?: number;
  total?: number;
  calculatedPercent?: number | null;
  rejectionReason?: string | null;
};

const MAX_DIAGNOSTIC_EVENTS = 25;

export default function UploadingBar({
  xhr,
  expectedBytes,
  stage,
}: {
  xhr: XMLHttpRequest;
  expectedBytes: number;
  stage: "uploading" | "converting";
}) {
  const [progress, setProgress] = useState<number | null>(null);
  const startedAt = useRef(Date.now());
  const diagnosticEvents = useRef<UploadDiagnosticEvent[]>([]);

  useEffect(() => {
    startedAt.current = Date.now();
    diagnosticEvents.current = [];
    let reported = false;
    let sawInvalidProgress = false;
    const record = (event: UploadDiagnosticEvent) => {
      if (diagnosticEvents.current.length < MAX_DIAGNOSTIC_EVENTS - 1) {
        diagnosticEvents.current.push(event);
      } else if (diagnosticEvents.current.length < MAX_DIAGNOSTIC_EVENTS) {
        diagnosticEvents.current.push(event);
      } else {
        // Preserve the first events and the most recent event without allowing
        // a noisy progress stream to create an unbounded report.
        diagnosticEvents.current[MAX_DIAGNOSTIC_EVENTS - 1] = event;
      }
      console.debug("[chat-upload-debug]", event);
    };
    const progressHandler = (event: ProgressEvent) => {
      const { percent: calculatedPercent, rejectionReason } =
        analyzeUploadProgress(event, expectedBytes);
      if (calculatedPercent === null) sawInvalidProgress = true;
      record({
        type: "progress",
        elapsedMs: Date.now() - startedAt.current,
        lengthComputable: event.lengthComputable,
        loaded: event.loaded,
        total: event.total,
        calculatedPercent,
        rejectionReason,
      });
      setProgress(calculatedPercent);
    };
    const recordLifecycle = (event: Event) => {
      record({
        type: `upload:${event.type}`,
        elapsedMs: Date.now() - startedAt.current,
      });
    };
    const loadHandler = (event: Event) => {
      recordLifecycle(event);
      setProgress(100);
    };
    const requestLoadEndHandler = () => {
      if (reported) return;
      reported = true;
      const isIOSWebKit =
        /WebKit/i.test(navigator.userAgent) &&
        (/iPhone|iPad|iPod/i.test(navigator.userAgent) ||
          (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1));
      const diagnostics = {
        expectedBytes,
        durationMs: Date.now() - startedAt.current,
        status: xhr.status,
        sawInvalidProgress,
        isIOSWebKit,
        events: diagnosticEvents.current,
      };
      console.debug("[chat-upload-debug] complete", diagnostics);
    };

    xhr.upload.addEventListener("loadstart", recordLifecycle);
    xhr.upload.addEventListener("progress", progressHandler);
    xhr.upload.addEventListener("load", loadHandler);
    xhr.upload.addEventListener("error", recordLifecycle);
    xhr.upload.addEventListener("abort", recordLifecycle);
    xhr.upload.addEventListener("loadend", recordLifecycle);
    xhr.addEventListener("loadend", requestLoadEndHandler);

    (xhr as any).startUpload();

    return () => {
      xhr.upload.removeEventListener("loadstart", recordLifecycle);
      xhr.upload.removeEventListener("progress", progressHandler);
      xhr.upload.removeEventListener("load", loadHandler);
      xhr.upload.removeEventListener("error", recordLifecycle);
      xhr.upload.removeEventListener("abort", recordLifecycle);
      xhr.upload.removeEventListener("loadend", recordLifecycle);
      xhr.removeEventListener("loadend", requestLoadEndHandler);
    };
  }, [expectedBytes, xhr]);

  const displayedProgress = stage === "converting" ? null : progress;
  const isIndeterminate = displayedProgress === null;

  return (
    <div className="shrink-0 mx-2 rounded-xl border border-primary bg-message px-4 py-3 shadow-lg flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 font-medium">
        {stage === "converting" ? (
          <FormattedMessage
            id="chat__media_converting"
            defaultMessage="Media converting..."
          />
        ) : (
          <FormattedMessage id="uploader__please_be_patient" />
        )}
        <span className="shrink-0 text-primary tabular-nums">
          {isIndeterminate ? "…" : displayedProgress.toFixed(0) + "%"}
        </span>
      </div>
      <div
        className="w-full h-3 rounded-full overflow-hidden border border-tabs bg-chatbg"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={isIndeterminate ? undefined : displayedProgress}
      >
        <div
          className={
            "bg-primary h-full " +
            (isIndeterminate
              ? "chat-upload-indeterminate"
              : "transition-[width] duration-150")
          }
          style={
            isIndeterminate ? undefined : { width: displayedProgress + "%" }
          }
        ></div>
      </div>
    </div>
  );
}
